import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
    catchError, exhaustMap, filter, interval, map, mergeMap, of, switchMap, takeUntil, tap,
} from 'rxjs';

import {
    BlueSageTerminalPushType,
    CancelBlueSageTerminalPushGQL,
    CompleteBlueSagePreauthGQL,
    GetBlueSageTerminalPushGQL,
    GetBlueSageTerminalPushStatusGQL,
    PushBlueSageToTerminalGQL,
    TransactionStage,
    UpdateTransactionGQL,
} from 'src/generated/graphql.generated';

import { JobFinancialsActions } from '../../job/job-financials/job-financials.actions';
import { BlueSageTerminalPickerActions } from '../terminal-picker/bluesage-terminal-picker.actions';


import { BlueSageTerminalPushActions } from './bluesage-terminal-push.actions';
import { blueSageTerminalPushFeature } from './bluesage-terminal-push.feature';
import { BlueSagePushKind } from './bluesage-terminal-push.state';

/**
 * Which push type an action means.
 *
 * The two buttons carry it in their identity; `pushRequested` carries it as a
 * field, because a retry and the payment dialog's create-then-push both have to
 * say what they are replaying rather than being inferred from a click.
 */
function kindOf(action: { type: string; kind?: BlueSagePushKind }): BlueSagePushKind {
    if (action.kind) { return action.kind; }
    if (action.type === BlueSageTerminalPushActions.authorizeButtonClicked.type) { return 'auth'; }
    if (action.type === BlueSageTerminalPushActions.completePreauthConfirmed.type) { return 'ticket'; }
    return 'sale';
}

/**
 * Did this outcome settle with NOTHING taken?
 *
 * ⚠ Deliberately narrow. `cancelled` and `declined` both mean the device
 * finished and no money moved. A `timeout` does NOT — the device never
 * answered, so nothing is known, and closing a transaction whose fate is
 * unknown is a far worse defect than leaving it pending. That is what the sweep
 * is for.
 *
 * A successful CANCEL is included on the same footing: the cancel route only
 * answers once the push is genuinely stopped, and `push_not_cancellable` comes
 * back as an error rather than a success.
 */
function settledWithNothingTaken(action: {
    type: string;
    push?: { settled: boolean; status?: string | null };
    result?: { push: { settled: boolean; status?: string | null } };
    status?: string | null | { push?: { settled: boolean; status?: string | null } };
}): boolean {
    if (action.type === BlueSageTerminalPushActions.cancelSuccess.type) { return true; }

    const push = action.push
        ?? action.result?.push
        ?? (typeof action.status === 'object' ? action.status?.push : undefined);

    return Boolean(push?.settled)
        && (push?.status === 'cancelled' || push?.status === 'declined');
}

/** How often to ask the device what happened. Fast enough to feel live. */
const POLL_INTERVAL_MS = 2_000;
/** How often the elapsed counter re-renders. */
const TICK_INTERVAL_MS = 1_000;

/**
 * Send an amount to a terminal.
 *
 * `exhaustMap`, deliberately — the opposite choice from every read in this
 * feature. This puts a live amount on a card reader in front of a customer, so
 * a double-click must not become two attempts. The derived idempotency key
 * already makes a second attempt a replay rather than a second charge, but the
 * UI should not be relying on that to stay correct.
 */
export const pushBlueSageToTerminal = createEffect((
    actions$ = inject(Actions),
    pushGQL = inject(PushBlueSageToTerminalGQL),
) => {
    return actions$.pipe(
        ofType(
            BlueSageTerminalPushActions.sendToTerminalButtonClicked,
            BlueSageTerminalPushActions.authorizeButtonClicked,
            BlueSageTerminalPushActions.pushRequested,
        ),
        exhaustMap((action) => of(action).pipe(
            filter(() => kindOf(action) !== 'ticket'),
            mergeMap(() => {
                // Two separately labelled buttons map onto the contract's two push
                // types. `ticket` is deliberately unreachable from here — completing
                // a hold goes through `completeBlueSagePreauth`, which reads the
                // reference off the transaction instead of trusting a caller.
                const authorizing = kindOf(action) === 'auth';

                return pushGQL.mutate({
                    transactionId: action.transactionId,
                    epi: action.epi,
                    type: authorizing
                        ? BlueSageTerminalPushType.Auth
                        : BlueSageTerminalPushType.Sale,
                    amount: action.amountMinor,
                }).pipe(
                    map((res) => BlueSageTerminalPushActions.pushSuccess({
                        result: res.data.pushBlueSageToTerminal,
                        kind: authorizing ? 'auth' : 'sale',
                    })),
                );
            }),
            catchError((error) => of(BlueSageTerminalPushActions.pushError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

/**
 * Complete an authorization the transaction already holds.
 *
 * Takes no reference: the transaction carries its own preauth ref, so nobody
 * has to name which hold they are settling. `exhaustMap` for the same reason as
 * the push above.
 */
export const completeBlueSagePreauth = createEffect((
    actions$ = inject(Actions),
    completeGQL = inject(CompleteBlueSagePreauthGQL),
) => {
    return actions$.pipe(
        ofType(
            BlueSageTerminalPushActions.completePreauthConfirmed,
            BlueSageTerminalPushActions.pushRequested,
        ),
        exhaustMap((action) => of(action).pipe(
            filter(() => kindOf(action) === 'ticket'),
            mergeMap(() => completeGQL.mutate({
                transactionId: action.transactionId,
                epi: action.epi,
                amount: action.amountMinor,
            }).pipe(
                map((res) => BlueSageTerminalPushActions.completeSuccess({
                    result: res.data.completeBlueSagePreauth,
                })),
            )),
            catchError((error) => of(BlueSageTerminalPushActions.completeError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

/**
 * Retry.
 *
 * Reuses the SAME transaction and the same EPI, so the derived idempotency key
 * is identical and the middleware replays the original push rather than putting
 * a second prompt on a live reader. It never creates a second transaction —
 * that is what makes "Try again" safe to offer on a money screen at all.
 */
export const retryBlueSageTerminalPush = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(BlueSageTerminalPushActions.tryAgainButtonClicked),
        concatLatestFrom(() => store.select(blueSageTerminalPushFeature.selectBlueSageTerminalPushState)),
        filter(([ , state ]) => Boolean(state.transactionId && state.epi && state.amountMinor)),
        map(([ , state ]) => BlueSageTerminalPushActions.pushRequested({
            transactionId: state.transactionId,
            epi: state.epi,
            amountMinor: state.amountMinor,
            kind: state.kind,
        })),
    );
}, { functional: true, dispatch: true });

/**
 * Poll the push while the customer still has the device.
 *
 * `switchMap` over the trigger because a NEW push must abandon the previous
 * push's poller — otherwise two timers race and the panel flips between two
 * pushes' statuses. The inner interval stops when the panel goes away so a
 * closed dialog does not keep querying a payment host forever.
 *
 * Polling is what keeps the CRM-side device lock alive too: the server refreshes
 * the claim on each read and releases it the moment the push settles.
 */
export const pollBlueSageTerminalPush = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    getPushGQL = inject(GetBlueSageTerminalPushGQL),
) => {
    return actions$.pipe(
        ofType(
            BlueSageTerminalPushActions.pushSuccess,
            BlueSageTerminalPushActions.completeSuccess,
        ),
        filter(({ result }) => Boolean(result.push.pushId) && !result.push.settled),
        switchMap(({ result }) => interval(POLL_INTERVAL_MS).pipe(
            takeUntil(actions$.pipe(ofType(
                BlueSageTerminalPushActions.panelDismissed,
                BlueSageTerminalPushActions.doneButtonClicked,
            ))),
            concatLatestFrom(() => store.select(blueSageTerminalPushFeature.selectPhase)),
            // Stop asking the moment it is no longer on the device.
            filter(([ , phase ]) => phase === 'waiting'),
            mergeMap(() => getPushGQL.fetch(
                { pushId: result.push.pushId }, { fetchPolicy: 'no-cache' },
            ).pipe(
                map((res) => BlueSageTerminalPushActions.pushStatusPolled({
                    push: res.data.blueSageTerminalPush,
                })),
                // A failed poll is NOT a failed payment. The customer may be mid-tap
                // and the next tick may answer perfectly; changing the phase here
                // would report a decline that never happened.
                catchError((error) => of(BlueSageTerminalPushActions.pushStatusPollError({ error }))),
            )),
        )),
    );
}, { functional: true, dispatch: true });

/**
 * Follow a push through its TRANSACTION until the middleware has answered.
 *
 * Since 2026-09-03 the push mutations return the moment the push is handed to
 * the middleware — a browser request that stayed open for the customer's
 * whole interaction was cut by the production ingress at 60 seconds, and
 * every push that outlived that was reported as a failure while the device,
 * the backend and the middleware all carried on. So the mutation's answer
 * carries no push id, and this is what keeps the panel honest: every two
 * seconds it asks the transaction where the push is.
 *
 * It stops the moment the outcome is anything but `in_flight`. A `failed`
 * outcome is re-dispatched as `pushRefusedAfterStart`, which the reducer and
 * the payment dialog's cancel logic treat exactly as they treated the same
 * refusal when it arrived on the mutation. `lost` and `timeout` settle the
 * panel on their own screens — nothing is known, and the copy says so.
 */
export const followBlueSageTerminalPush = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    statusGQL = inject(GetBlueSageTerminalPushStatusGQL),
) => {
    return actions$.pipe(
        ofType(
            BlueSageTerminalPushActions.pushSuccess,
            BlueSageTerminalPushActions.completeSuccess,
        ),
        // Only a push the middleware has NOT yet named. Once a push id exists the
        // push-id poller above owns it, and two pollers would race each other.
        filter(({ result }) => !result.push.pushId && !result.push.settled),
        switchMap(({ result }) => {
            let noneTicks = 0;
            return interval(POLL_INTERVAL_MS).pipe(
            takeUntil(actions$.pipe(ofType(
                BlueSageTerminalPushActions.panelDismissed,
                BlueSageTerminalPushActions.doneButtonClicked,
                BlueSageTerminalPushActions.pushRefusedAfterStart,
            ))),
            concatLatestFrom(() => [
                store.select(blueSageTerminalPushFeature.selectPhase),
                store.select(blueSageTerminalPushFeature.selectKind),
            ]),
            filter(([ , phase ]) => phase === 'waiting'),
            // `exhaustMap`: a read that is still in flight is not joined by the
            // next tick's. The status read may ask the middleware about the push
            // and write the answer onto the row, and stacking those every two
            // seconds meant a dozen concurrent reads racing to write one row.
            exhaustMap(([ , , kind ]) => statusGQL.fetch(
                { transactionId: result.transactionId }, { fetchPolicy: 'no-cache' },
            ).pipe(
                map((res) => {
                    const status = res.data.blueSageTerminalPushStatus;
                    // `none` means the row never received its in-flight marker — the
                    // write failed — so the server can never promote it to `lost`.
                    // Give it the same window here rather than counting forever.
                    if (status.outcome === 'none') {
                        noneTicks += 1;
                        if (noneTicks >= NONE_TICKS_BEFORE_LOST) {
                            return BlueSageTerminalPushActions.pushStatusChecked({
                                status: { ...status, outcome: 'lost', push: { ...status.push, status: 'timeout', settled: true } },
                            });
                        }
                    }
                    return status.outcome === 'failed'
                        ? BlueSageTerminalPushActions.pushRefusedAfterStart({
                            message: status.failure
                                || 'That could not be sent to the terminal.',
                            kind,
                        })
                        : BlueSageTerminalPushActions.pushStatusChecked({ status });
                }),
                // A failed read is NOT a failed payment — the next tick may answer.
                catchError((error) => of(BlueSageTerminalPushActions.pushStatusCheckError({ error }))),
            )),
            );
        }),
    );
}, { functional: true, dispatch: true });

/** How many consecutive `none` reads before the panel gives the push up as lost (~30 s). */
const NONE_TICKS_BEFORE_LOST = 15;

/**
 * Take the operator to the one place a push the panel lost sight of will
 * surface if the customer completed on the device.
 */
export const openBlueSageUnmatchedPayments = createEffect((
    actions$ = inject(Actions),
    router = inject(Router),
) => {
    return actions$.pipe(
        ofType(BlueSageTerminalPushActions.openUnmatchedPaymentsClicked),
        tap(() => router.navigate([ '/business', 'unmatched-payments' ])),
    );
}, { functional: true, dispatch: false });

/** Drive the elapsed counter and the tip countdown while a push is live. */
export const tickBlueSageTerminalPush = createEffect((
    actions$ = inject(Actions),
) => {
    return actions$.pipe(
        // Starts on the CLICK, not on the response. The publish long-polls for
        // the whole customer interaction, so a ticker that waited for the
        // response would only start once the customer had already finished.
        //
        // `pushPreparing` is in here for the same reason one step earlier: the
        // wait an operator is sitting through starts when they press Pay, not
        // when the transaction finishes saving.
        ofType(
            BlueSageTerminalPushActions.pushPreparing,
            BlueSageTerminalPushActions.sendToTerminalButtonClicked,
            BlueSageTerminalPushActions.authorizeButtonClicked,
            BlueSageTerminalPushActions.completePreauthConfirmed,
            BlueSageTerminalPushActions.pushRequested,
        ),
        switchMap(() => interval(TICK_INTERVAL_MS).pipe(
            takeUntil(actions$.pipe(ofType(
                BlueSageTerminalPushActions.panelDismissed,
                BlueSageTerminalPushActions.doneButtonClicked,
                BlueSageTerminalPushActions.pushError,
                BlueSageTerminalPushActions.completeError,
                BlueSageTerminalPushActions.pushRefusedAfterStart,
            ))),
            map(() => BlueSageTerminalPushActions.elapsedTicked()),
        )),
    );
}, { functional: true, dispatch: true });

/**
 * Cancel a push the device has not finished with.
 *
 * `exhaustMap` so a second click cannot queue a second cancel. A failure here is
 * frequently NOT a failure: `push_not_cancellable` means the terminal already
 * released it, which is why the reducer leaves the phase alone and lets the
 * poller report the real outcome.
 */
export const cancelBlueSageTerminalPush = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    cancelGQL = inject(CancelBlueSageTerminalPushGQL),
) => {
    return actions$.pipe(
        ofType(BlueSageTerminalPushActions.cancelButtonClicked),
        exhaustMap((action) => of(action).pipe(
            concatLatestFrom(() => store.select(blueSageTerminalPushFeature.selectPushId)),
            filter(([ , pushId ]) => Boolean(pushId)),
            mergeMap(([ , pushId ]) => cancelGQL.mutate({ pushId }).pipe(
                map((res) => BlueSageTerminalPushActions.cancelSuccess({
                    status: res.data.cancelBlueSageTerminalPush.status ?? null,
                })),
            )),
            catchError((error) => of(BlueSageTerminalPushActions.cancelError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

/**
 * Remember a terminal the operator actually SENT to.
 *
 * Reacting to the push action rather than the picker's selection, because a
 * device someone merely clicked on is not a device they work from.
 */
export const rememberBlueSageTerminalUsed = createEffect((
    actions$ = inject(Actions),
) => {
    return actions$.pipe(
        ofType(
            BlueSageTerminalPushActions.sendToTerminalButtonClicked,
            BlueSageTerminalPushActions.authorizeButtonClicked,
            BlueSageTerminalPushActions.completePreauthConfirmed,
            BlueSageTerminalPushActions.pushRequested,
        ),
        map(({ epi }) => BlueSageTerminalPickerActions.terminalUsed({ epi })),
    );
}, { functional: true, dispatch: true });

/**
 * The settled push carried by whichever settlement action this is, or
 * `undefined` when it has not settled.
 *
 * `pushStatusPolled` carries `push` directly; `pushSuccess` and
 * `completeSuccess` carry it under `result`. Reading both is what lets one
 * effect answer for every way a push can finish.
 */
function settledPushOf(action: {
    push?: { settled?: boolean | null };
    result?: { push?: { settled?: boolean | null } };
    status?: { push?: { settled?: boolean | null } };
}): boolean {
    return Boolean((action.push ?? action.result?.push ?? action.status?.push)?.settled);
}

/**
 * Refresh the picker once a push settles.
 *
 * The device lock is released server-side on settle, so the roster's `busy`
 * badge is stale the moment this happens — and a device still showing "In use"
 * after the customer has walked away is the picker lying about the one thing it
 * disables Send for.
 */
export const refreshTerminalsAfterPush = createEffect((
    actions$ = inject(Actions),
) => {
    return actions$.pipe(
        ofType(
            BlueSageTerminalPushActions.pushStatusPolled,
            BlueSageTerminalPushActions.pushStatusChecked,
            // ⚠ The POLL is not the only way a push settles, and keying on it
            // alone left the picker lying. The poller only starts when the push
            // came back UNsettled (`filter(... && !result.push.settled)` above),
            // so a push the middleware resolved inline never produced a
            // `pushStatusPolled` at all — and the device stayed "In use" until
            // somebody pressed Refresh. Cancelling has the same shape: the device
            // is free the moment the cancel succeeds and nothing polls after it.
            BlueSageTerminalPushActions.pushSuccess,
            BlueSageTerminalPushActions.completeSuccess,
            BlueSageTerminalPushActions.cancelSuccess,
        ),
        // A cancel that succeeded IS settled — the route only answers once the
        // push is genuinely stopped. Everything else has to say so itself.
        filter((action) =>
            action.type === BlueSageTerminalPushActions.cancelSuccess.type
            || Boolean(settledPushOf(action))),
        // Its OWN action, never the picker's Refresh BUTTON action. Two UI
        // elements in different surfaces that trigger the same behaviour still get
        // separate actions; they may share an effect handler, and this one does.
        map(() => BlueSageTerminalPickerActions.terminalReleased()),
    );
}, { functional: true, dispatch: true });


/**
 * Close the transaction when the device settled the push without taking anything.
 *
 * A push that is **cancelled** — on the device, or because the reader dropped
 * off its Connect screen — settles with `transaction: null`: no card was
 * touched and no money moved. A **declined** one is the same in this respect.
 * Either way the CRM row that was created to push against stayed `pending`
 * forever, which on the job is indistinguishable from a payment still in
 * flight. Three of YMM's four "stuck pending" rows on 2026-08-28 were exactly
 * this — verified against the middleware afterwards, all three
 * `status: "cancelled", transaction: null`, nothing charged.
 *
 * ⚠ Deliberately NOT on `timeout`, and not on an unrecognised settled status.
 * A timeout means the device never answered and **nothing is known**; closing a
 * transaction whose fate is unknown is a far worse defect than leaving it
 * pending, and the sweep exists precisely to resolve that case later.
 *
 * The stage becomes `cancelled` rather than the row being deleted: nothing was
 * charged, but somebody DID try to take a payment, and that is worth keeping.
 *
 * `exhaustMap` because it is a write and the poller can fire again before it
 * returns.
 */
export const cancelBlueSageTransactionWhenNothingWasTaken = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    updateTransactionGQL = inject(UpdateTransactionGQL),
) => {
    return actions$.pipe(
        // ⚠ THREE sources, not one.
        //
        // The poller was the only one, and it misses two cases outright:
        //
        //  - `POST /v1/terminal-pushes` long-polls the whole customer
        //    interaction, so a card declined or cancelled AT THE READER inside
        //    that window comes back on the push's own response, already settled.
        //    `pollBlueSageTerminalPush` skips those (`!result.push.settled`), so
        //    no `pushStatusPolled` ever follows and the row stayed `pending`.
        //  - Pressing **Cancel** flips the phase to `cancelled`, and the poller's
        //    `filter(phase === 'waiting')` then drops every remaining tick — so
        //    the panel said "Cancelled — nothing was charged" while the row on
        //    the job stayed `pending`, which is the exact pair this effect exists
        //    to keep honest.
        ofType(
            BlueSageTerminalPushActions.pushStatusPolled,
            // NOT `pushStatusChecked`: the status read closes a cancelled or
            // declined sale or auth server-side as it records the answer, so a
            // client write here would be a second write of the same row.
            BlueSageTerminalPushActions.pushSuccess,
            BlueSageTerminalPushActions.completeSuccess,
            BlueSageTerminalPushActions.cancelSuccess,
        ),
        filter((action) => settledWithNothingTaken(action)),
        exhaustMap((action) => of(action).pipe(
            concatLatestFrom(() => [
                store.select(blueSageTerminalPushFeature.selectTransactionId),
                store.select(blueSageTerminalPushFeature.selectKind),
            ]),
            // ⚠ NEVER for a `ticket`. A completion push carries the HOLD'S OWN
            // transaction id — the template hands `openPreauthTransactionId`
            // straight to `completePreauthConfirmed` — so cancelling a completion
            // would mark the live authorization `cancelled`. It would then vanish
            // from both Complete and Release, because each requires
            // `stage === 'pending'`, leaving a customer's money frozen on their
            // card with no path out of the UI at all.
            //
            // A `sale` or `auth` push owns the row it is pushing against, and
            // that row means nothing once the push settles with nothing taken.
            // A `ticket` does not own its row, and the hold is still live.
            filter(([ , transactionId, kind ]) => Boolean(transactionId) && kind !== 'ticket'),
            mergeMap(([ , transactionId ]) => updateTransactionGQL.mutate({
                transactionId,
                stage: TransactionStage.Cancelled,
            }).pipe(
                map((res) => BlueSageTerminalPushActions.unsettledTransactionCancelled({
                    data: res.data,
                })),
            )),
            catchError((error) =>
                of(BlueSageTerminalPushActions.unsettledTransactionCancelError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

/**
 * Show the closed row on the job without a reload.
 *
 * Its own effect rather than a second mapping inside the one above, per the
 * repo's rule that an effect calling a mutation is concerned with that mutation
 * and its success/error pair; what follows from the success belongs here.
 */
export const showCancelledBlueSageTransactionOnJob = createEffect((
    actions$ = inject(Actions),
) => {
    return actions$.pipe(
        ofType(BlueSageTerminalPushActions.unsettledTransactionCancelled),
        map(({ data }) => JobFinancialsActions.updateTransactionSuccess({ data })),
    );
}, { functional: true, dispatch: true });
